<template>
<div class="py-6 px-4 sm:px-6">

  <div>
    <h2 class="text-lg leading-6 font-medium text-gray-900">Online Payments</h2>
  </div>

  <div v-if="screen === 'chargesEnabled'">
    
    <!--
      You have submitted your account details and they have
      been approved by Stripe, you can now accept payments.
      AccountLink is no use to us here, so we should remind
      them of their account details and provide a link to
      the Stripe login page.
    -->

    <div class="-mx-4 mt-4 bg-green-50 p-4 sm:mx-0 sm:rounded-md">
      <div class="flex">
        <div class="hidden flex-shrink-0 sm:block">
          <CheckCircleSolidIcon class="w-6 h-6 text-green-400" />
        </div>
        <div class="sm:ml-3">
          <div class="flex items-center space-x-2 sm:space-x-0">
            <CheckCircleSolidIcon class="w-6 h-6 self-start text-green-400 sm:hidden" />
            <h3 class="text-sm font-medium text-green-800">Online Payments are enabled!</h3>
          </div>
          <div class="mt-2 prose prose-sm">
            <p>Your account is ready to accept online payments. Share your online payment URL to start receiving payments:</p>
            <div class="flex flex-row items-center space-x-3">
              <router-link
                :to="{ name: 'PayTutor', params: { id: $store.getters.user.PublicId, stub: $store.getters.user.Name.replace(/ /g, '-').toLowerCase() } }"
                class="leading-tight text-base underline"
              >{{ $store.state.user.PublicId }}-{{ $store.state.user.Name.replace(/ /g, '-').toLowerCase() }}/pay</router-link>
              <button v-if="navigatorAvailable" type="button" @click="onCopy" class="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-600">
                <DuplicateIcon class="w-5 h-5" />
                <span v-show="showCopiedText">Copied!</span>
              </button>
              <button v-if="navigatorAvailable" type="button" @click="onShare" class="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-600">
                <ShareIcon class="w-5 h-5" />
              </button>
            </div>
            <p class="text-xs">To manage your Stripe account, <a href="https://dashboard.stripe.com/login" target="_blank">sign in</a> to your account at stripe.com.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="mt-4 text-sm text-gray-600">
      Loading your recent payments…
    </div> -->

  </div>
  <div v-else-if="screen === 'detailsSubmitted'" class="text-sm">

    <!--
      You have completed the submission of your account to Stripe,
      but the ability to take charges have not yet been implemented
      on your account. There is very little we can do here, other
      than allow them to generate a new AccountLink which will let
      them see and ammend their submission. However, it may be
      better to guide them to the Stripe login page where they
      can login, see their submission, as well as any other
      outstanding jobs to do. Let's do the latter. We should,
      however, remind them of their account details.
    -->

    <div class="-mx-4 mt-4 bg-blue-50 p-4 sm:mx-0 sm:rounded-md">
      <div class="flex">
        <div class="hidden flex-shrink-0 sm:block">
          <InformationCircleIcon class="w-6 h-6 text-blue-400" />
        </div>
        <div class="sm:ml-3">
          <div class="flex items-center space-x-2 sm:space-x-0">
            <InformationCircleIcon class="w-6 h-6 flex-shrink-0 self-start text-blue-400 sm:hidden" />
            <h3 class="flex-1 text-sm font-medium text-blue-800">Your submission is being reviewed</h3>
            <RefreshIcon @click="refreshAccountStatus" class="flex-shrink-0 self-start w-5 h-5 text-gray-400 cursor-pointer" />
          </div>
          <div class="mt-2 prose prose-sm">
            <p>Your details have been received but must undergo a review process before you can start to take payments. To check the status of your submission, or make any ammendments, sign in to your Stripe account.</p>
            <TailwindButton
              @click="signInToStripe"
              theme="stripe"
              class="px-6"
            >
              <span>
                Sign in to
              </span>
              <StripeLogo class="mt-px h-6" alt="Stripe" />
            </TailwindButton>
            <p class="text-xs">If you need help, check out our guide on <router-link :to="stripeGuideLink">connecting to Stripe</router-link>; alternatively, <a href="mailto:support@thetutorindex.com">get in touch</a> and we’ll be happy to assist.</p>
          </div>
        </div>
      </div>
    </div>

  </div>
  <div v-else-if="screen === 'connected'">

    <!--
      You have a Stripe Account ID, but have not finished
      submitting your details. Using a newly generated
      AccountLink you can resume your account creation
      with Stripe.
    -->

    <div class="-mx-4 mt-4 bg-yellow-50 p-4 sm:mx-0 sm:rounded-md">
      <div class="flex">
        <div class="hidden flex-shrink-0 sm:block">
          <ExclamationIcon class="w-6 h-6 text-yellow-400" />
        </div>
        <div class="sm:ml-3">
          <div class="flex items-center space-x-2 sm:space-x-0">
            <ExclamationIcon class="flex-shrink-0 self-start w-6 h-6 text-yellow-400 sm:hidden" />
            <h3 class="text-sm font-medium text-yellow-800">Complete your account setup</h3>
          </div>
          <div class="mt-2 prose prose-sm">
            <p>You still need to complete and submit the Stripe onboarding process in order to proceed with the creation of your Stripe account.</p>
            <TailwindButton
              @click="resumeAccountSetup"
              theme="stripe"
              class="px-6"
            >
              Complete Account Setup…
            </TailwindButton>
            <p class="text-xs">
              If you need help, check out our guide on <router-link :to="stripeGuideLink">connecting to Stripe</router-link>; alternatively, <a href="mailto:support@thetutorindex.com">get in touch</a> and we’ll be happy to assist.
            </p>
          </div>
        </div>
      </div>
    </div>


  </div>
  <div v-else-if="screen === 'loading'">

    <!-- 
      You have a Stripe account, so we need to just
      lookup the details of the account. Enjoy this loading
      screen while you wait.
    -->

    <div class="pt-10 pb-6 flex flex-col justify-center items-center space-y-3 text-gray-600">
      <Spinner color="#999" size="1.2em" />
      <span class="text-sm">
        Loading your Stripe account…
      </span>
    </div>

  </div>
  <div v-else class="prose prose-sm text-gray-500">

    <!-- 
      You have not had any interaction with Stripe, therefore
      you can get started by creating a new Account and
      embarking on the AccountLink journey.
    -->

    <!-- <h3>Configure Online Payments</h3>
    <p>Enable online payments and make it simple for your clients to pay you quickly and securely.</p> -->
    <p>Receive your lesson fees and other payments via your personal, online payment page.</p>

    <p class="pl-4 border-l-2 border-l-gray-200 font-medium">
      In a 2022 survey, we asked clients what their preferred payment method was. <strong class="font-extrabold whitespace-nowrap">4 out of 5</strong> said they would prefer to pay with card rather than use bank transfer.
    </p>
    <p>
      Taking payments online offers a lot of advantages over more traditional methods, such as bank transfer or cash-in-hand. Learn more about <router-link :to="{ name: 'Article', params: { url: 'online-payments-for-tutors' } }">how Online Payments work</router-link> and the advantages it can bring for you and your clients.
    </p>
    <p>
      Check out our <router-link :to="{ name: 'Article', params: { url: 'getting-started-with-online-payments' } }">quick start guide</router-link> and <router-link :to="{ name: 'Article', params: { url: 'faqs-about-online-payments' } }">frequently asked questions</router-link>, and if you need any assistance, please don’t hestitate to email us at <a href="mailto:support@thetutorindex.com">support@thetutorindex.com</a>.
    </p>

    <!-- <ul>
      <li>Allow your clients to pay in a way suits them, including: Apple Pay, Google Pay, Credit Card, and Debit Card.</li>
      <li>Reassure your clients they’re paying the right person – no more nervously double-checking bank account details are typed correctly.</li>
      <li>Take advantage of an advanced payment dashboard, with all your payments tracked in one place.</li>
      <li>Powered by Stripe™ – a global leader in handling secure, online payments.</li>
      <!- <li>Take payments in a number of global currencies, automatically converted to your local currency.</li> ->
      <li>Stand out from the crowd with a special badge that lets prospective clients know how professional you are.</li>
      <!- <li>Make your tax return easy by connecting Stripe to Quickbooks, Xero, and more.</li> ->
      <li>Get going without setup fees, and you can be up and running within minutes.</li>
    </ul> -->

    <p>Please note, all transactions incur a 2.5% application fee, plus Stripe fees.</p>
    <p>By continuing you are agreeing to the <router-link :to="{ name: 'OnlinePaymentTerms' }">Online Payments Terms & Conditions</router-link>.</p>

    <div v-if="!hasPassedIdCheck" class="rounded-md bg-red-50 mb-4 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <ExclamationIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800 m-0">
            You must complete our ID Check
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <p class="mt-0">
              As part of our safeguarding policy, tutors must first complete our ID Check in order to enable and use Online Payments.
            </p>
            <TailwindButton @click="$router.push({ name: 'EditTutor-IdCheck' })">
              Complete ID Check
            </TailwindButton>
          </div>
        </div>
      </div>
    </div>

    <TailwindButton
      @click="connectWithStripe"
      theme="stripe"
      class="px-6"
      :loading="connecting"
      :disabled="!hasPassedIdCheck || connecting"
    >
      <span>
        Connect with
      </span>
      <StripeLogo class="mt-px h-6" alt="Stripe" />
    </TailwindButton>
  </div>
  
  
</div>
</template>

<script>
import StripeServices from '@/services/StripeServices'
import ConnectWithStripe from '../components/ConnectWithStripe.vue'
const stripeService = new StripeServices()
import { CheckCircleIcon, XCircleIcon, } from '@vue-hero-icons/outline'
import { ShareIcon, ExclamationIcon, InformationCircleIcon, CheckCircleIcon as CheckCircleSolidIcon, DuplicateIcon, RefreshIcon, } from '@vue-hero-icons/solid'
import Spinner from './Spinner.vue'
import TailwindButton from './TailwindButton.vue'
import StripeLogo from '@/components/StripeLogo.vue'

export default {
  components: { 
    ConnectWithStripe,
    CheckCircleIcon,
    XCircleIcon,
    ShareIcon,
    Spinner,
    ExclamationIcon,
    TailwindButton,
    InformationCircleIcon,
    CheckCircleSolidIcon,
    DuplicateIcon,
    StripeLogo,
    RefreshIcon,
  },
  data() {
    return {
      detailsSubmittedToStripe: false,
      stripeChargesEnabled: false,
      stripeStatusReceived: false,
      showCopiedText: false,
      stripeGuideLink: { name: 'Article', params: { url: 'getting-started-with-online-payments' } }, 
      connecting: false,
    }
  },
  methods: {
    async checkStripeStatus() {
      const response = await stripeService.checkAccountStatus()
      this.detailsSubmittedToStripe = response.data.DetailsSubmittedToStripe
      this.stripeChargesEnabled = response.data.StripeChargesEnabled
      this.stripeStatusReceived = true
    },
    async connectWithStripe() {
      this.connecting = true
      const response = await stripeService.connectWithStripe()
      window.location = response.data
    },
    async redirectToAccountLink() {
      const response = await stripeService.generateAccountLink()
      window.location = response.data
    },
    onShare() {
      navigator.share({
        title: `Make a secure, online payment to ${this.$store.getters.user.Name}`,
        url: `https://www.thetutorindex.com/${ this.$store.state.user.PublicId }-${ this.$store.state.user.Name.replace(/ /g, '-').toLowerCase() }/pay`
      })
    },
    onCopy() {
      navigator.clipboard.writeText(
        `https://www.thetutorindex.com/${ this.$store.state.user.PublicId }-${ this.$store.state.user.Name.replace(/ /g, '-').toLowerCase() }/pay`
      )
      this.showCopiedText = true
      setTimeout(() => this.showCopiedText = false, 1000)
    },
    resumeAccountSetup() {
      this.redirectToAccountLink()
    },
    signInToStripe() {
      window.open('https://dashboard.stripe.com/login', '_blank')
    },
    refreshAccountStatus() {
      this.stripeStatusReceived = false
      this.checkStripeStatus()
    },
  },
  computed: {
    screen() {
      
      // We know straight away if the user has ever started
      // the Stripe connection process (i.e. presence of
      // a ConnectedStripeAccount record) so we can immediately
      // know whether to show the intro screen.
      if (!this.hasStripeAccount) {
        return 'default'
      }

      // If the user has a ConnectedStripeAccount record, we
      // will fetch the up to date status directly from Stripe,
      // but this will take a moment so we'll show a loading page.
      if (!this.stripeStatusReceived) {
        return 'loading'
      }

      // Once we have a response from Stripe we can determine
      // which screen needs to be shown.
      if (this.stripeChargesEnabled) {
        return 'chargesEnabled'
      }
      else if (this.detailsSubmittedToStripe) {
        return 'detailsSubmitted'
      }
      else {
        return 'connected'
      }
    },
    navigatorAvailable() {
      return navigator.share !== undefined
    },
    hasStripeAccount() {
      return this.$store.getters.user?.HasStripeAccount ?? false
    },
    hasPassedIdCheck() {
      return this.$store.getters.user?.IdCheckStatus === 'verified'
    }
  },
  mounted() {
    if (this.hasStripeAccount) {
      this.checkStripeStatus()
    }
  }
}
</script>

<style>

</style>